import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useField, UseFieldConfig } from 'react-final-form';

import { DEFAULT_FILTERS_DEBOUNCE_TIMEOUT_IN_MS } from '../constants';

export const useDebouncedField = <T = string>(name: string, config?: UseFieldConfig<T, T>) => {
  const field = useField<T>(name, config);
  const { value, onChange } = field.input;
  const [isTyping, setTyping] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  // reset state when form get reset
  if (!isTyping && tempValue && !value) {
    setTempValue(value);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFormValue = useCallback(
    debounce((newValue: T) => {
      if (onChange) {
        onChange(newValue);
      }
      setTyping(false);
    }, DEFAULT_FILTERS_DEBOUNCE_TIMEOUT_IN_MS),
    []
  );

  const onChangeHandler = (newValue: T) => {
    /**
     * skip debounce when value was not changed by manual typing
     * (eq. changed by autocomplete, pasted from clipboard, etc)
     */
    if (!isTyping && Math.abs(String(newValue).length - String(tempValue).length) > 1) {
      setTempValue(newValue);
      onChange(newValue);
      return;
    }

    setTempValue(newValue);
    setTyping(true);
    updateFormValue(newValue);
  };

  return {
    input: {
      ...field.input,
      value: tempValue,
      onChange: onChangeHandler,
    },
    meta: field.meta,
  };
};
